import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Info, Plus, Search, StickyNote, X } from "lucide-react";
import { Badge, Textarea } from "@hub/ui";
import type { AuditField, CodeKind, VerdictId } from "../types";
import { KIND_LABEL } from "../data/codebook";
import { useCodeEntry, useCodebookSearch } from "../data/codebookSource";
import { codeDescription, codeTitle } from "../data/codeLabels";
import { reasonsFor } from "../data/fields";
import { reasonLabel } from "../data/reasons";
import type { MultiRowVerdict, MultiUnionRow } from "../data/verdicts";
import { SLOT_META } from "./compareParts";

// Interactive adjudication atoms for the case-review screen (HUB-504, S4–S7). Ported
// from the prototype's FieldCompare / MultiFieldCompare / CodePicker / CodebookModal
// controls, re-skinned onto @hub/ui + the design-system --ch-* tokens (teal = correct,
// red = neither, blue/plum = the blind A/B slots). No prototype CSS.
//
// BLIND-SAFE: the "Correct" toggles act on opaque A/B slots; nothing here names a
// slot as the agent vs the human coder.

/** Tone map for the four field verdicts → design-system semantic badges. */
const VERDICT_META: Record<
  VerdictId,
  { label: string; variant: "success" | "info" | "secondary" | "error" }
> = {
  both: { label: "Both correct", variant: "success" },
  a: { label: "Result A", variant: "info" },
  b: { label: "Result B", variant: "secondary" },
  neither: { label: "Neither correct", variant: "error" },
};

/** The resolved-field verdict chip shown in the collapsed summary row. */
export function VerdictChip({ verdict }: { verdict: VerdictId }) {
  const m = VERDICT_META[verdict];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

/** "Correct" pill for one blind slot — teal when marked correct. */
export function CorrectToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      title={checked ? "Marked correct" : "Mark this result correct"}
      className="inline-flex h-[26px] shrink-0 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-semibold transition-colors"
      style={{
        border: `1px solid ${checked ? "var(--ch-teal-500)" : "var(--border-default)"}`,
        background: checked ? "var(--ch-teal-500)" : "var(--bg-surface)",
        color: checked ? "#fff" : "var(--fg-tertiary)",
      }}
    >
      <Check
        className="size-3"
        style={{ opacity: checked ? 1 : 0.5 }}
        aria-hidden
      />{" "}
      Correct
    </button>
  );
}

/** "Neither is correct" pill — dashed until chosen, red when active. */
export function NeitherToggle({
  checked,
  onChange,
  full,
}: {
  checked: boolean;
  onChange: () => void;
  full?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      className={`inline-flex h-[26px] items-center justify-center gap-1 rounded-full px-3 text-[11.5px] font-semibold transition-colors ${full ? "w-full" : ""}`}
      style={{
        border: checked
          ? "1px solid var(--chip-neither-border)"
          : "1px dashed var(--border-strong)",
        background: checked ? "var(--chip-neither-bg)" : "var(--bg-surface)",
        color: checked ? "var(--chip-neither-fg)" : "var(--fg-tertiary)",
      }}
    >
      <X className="size-3" aria-hidden /> Neither is correct
    </button>
  );
}

/** A "note" toggle button that reflects whether a note is present. */
export function NoteButton({
  has,
  open,
  onClick,
}: {
  has: boolean;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={open}
      title="Add or edit a note"
      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-coronis border px-2 text-[11.5px] font-medium transition-colors"
      style={{
        borderColor:
          open || has ? "var(--chip-accent-border)" : "var(--border-default)",
        background: open || has ? "var(--chip-accent-bg)" : "var(--bg-surface)",
        color: open || has ? "var(--chip-accent-fg)" : "var(--fg-tertiary)",
      }}
    >
      <StickyNote className="size-3" aria-hidden /> {has ? "Note ✓" : "Note"}
    </button>
  );
}

/** Field-note textarea (shared across single + multi fields). */
export function FieldNote({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Textarea
      className="min-h-[44px] text-xs"
      placeholder="Optional note for this field…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Reason dropdown — a native <select> with two optgroups (this field's reasons +
 * the shared library), matching the prototype's RowReasonSelect/ReasonRow. Native so
 * the optgroup structure stays testable; tinted blue once a reason is picked.
 */
export function ReasonSelect({
  field,
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  field: AuditField;
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const shared = useMemo(
    () => reasonsFor(field).slice(field.reasons.length),
    [field],
  );
  return (
    <select
      aria-label={ariaLabel ?? `Reason for ${field.label}`}
      className="h-8 min-w-0 flex-1 rounded-coronis border px-2 text-[11.5px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coronis-blue-500"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: value ? "var(--chip-accent-bg)" : "var(--bg-surface)",
        borderColor: value
          ? "var(--chip-accent-border)"
          : "var(--border-default)",
      }}
    >
      <option value="">{placeholder ?? "Reason for this code…"}</option>
      <optgroup label={`${field.label} reasons`}>
        {field.reasons.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="General">
        {shared.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

export { reasonLabel };

/** Codebook detail modal — opened from any code chip. A plain fixed overlay (no
 *  portal) so it stays testable; Escape + backdrop close it. */
export function CodebookModal({
  code,
  onClose,
}: {
  code: string;
  onClose: () => void;
}) {
  // Navigable: the modal opens on the code that was clicked, but a reviewer
  // asking "what IS this" usually has a second question — the crosswalked ASA
  // code, or whatever the right code would have been. Closing the modal to look
  // one up loses the case context they were reading it against.
  const [shown, setShown] = useState(code);
  useEffect(() => setShown(code), [code]);

  // The server codebook. `acode` read a compiled-in map of ~130 entries, so this
  // modal — the one place a reviewer goes to find out what a code MEANS — said
  // "Uncatalogued code" for most real diagnoses.
  const lookup = useCodeEntry(shown);
  const entry = lookup.status === "found" ? lookup.entry : undefined;
  const related = lookup.status === "found" ? lookup.related : [];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Codebook — ${shown}`}
        className="flex max-h-[85vh] w-[560px] max-w-full flex-col overflow-hidden rounded-coronis border border-border bg-card shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[22px] font-semibold tracking-tight text-foreground">
                {shown || "—"}
              </span>
              <Badge variant="outline">
                {(entry?.kind && KIND_LABEL[entry.kind]) || "Code"}
              </Badge>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close codebook"
              className="inline-flex size-7 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
            >
              <X className="size-3.5" />
            </button>
          </div>
          <div className="mt-2 text-[15px] font-semibold text-foreground">
            {codeTitle(lookup)}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-5 py-4">
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Official descriptor
            </div>
            {/* The FULL descriptor, wrapped rather than clamped. This is the
                sentence a reviewer is judging the code against, and a CPT full
                descriptor routinely runs several lines — truncating it here is
                truncating the evidence. */}
            <p
              data-testid="code-descriptor"
              className="m-0 whitespace-pre-line text-[13px] leading-relaxed text-foreground"
            >
              {codeDescription(lookup)}
            </p>
            {entry?.title && entry.desc && entry.desc !== entry.title && (
              // Both descriptors, because they are different strings with
              // different uses: the short one is what every chip on the
              // compare screen shows, and a reviewer needs to see that the
              // chip they have been reading is an abbreviation of this.
              <p className="m-0 mt-2 text-[11.5px] leading-snug text-muted-foreground">
                Short descriptor: {entry.title}
              </p>
            )}
          </div>
          {entry?.base != null && (
            <div
              className="flex items-center gap-2 rounded-coronis px-3 py-2"
              style={{
                background: "var(--chip-accent-bg)",
                border: "1px solid var(--chip-accent-border)",
              }}
            >
              <Info
                className="size-3.5 shrink-0"
                style={{ color: "var(--chip-accent-fg)" }}
              />
              <span className="text-[12.5px] text-foreground">
                ASA base units: <strong>{entry.base}</strong> · total = base +
                time units + modifying units
              </span>
            </div>
          )}
          {related.length > 0 && (
            // The crosswalk the server already sends with an exact hit, which
            // the lookup used to discard. "Which ASA codes may be billed with
            // this CPT" is the question a reviewer judging an anesthesia code
            // is actually asking, and no descriptor answers it.
            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Billable anesthesia codes for this procedure
              </div>
              <ul className="flex flex-col gap-1">
                {related.map((r) => (
                  <li key={r.code}>
                    <button
                      type="button"
                      onClick={() => setShown(r.code)}
                      className="flex w-full items-baseline gap-2 rounded-coronis px-2 py-1 text-left hover:bg-muted"
                    >
                      <span className="font-mono text-[12.5px] font-semibold text-foreground">
                        {r.code}
                      </span>
                      <span className="truncate text-[12px] text-muted-foreground">
                        {r.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CodebookLookupField onPick={setShown} current={shown} />

          <div className="text-[11px] text-muted-foreground">
            Reference only — verify against the current CPT, ICD-10-CM, and ASA
            crosswalk edition.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Free-text codebook search inside the modal.
 *
 * Distinct from CodePicker, which SELECTS a code into a verdict. This one only
 * navigates the reference — a reviewer looking up what a code means often has a
 * follow-up question ("what is the code for a screening colonoscopy?"), and the
 * only way to ask it before was to close the modal and lose the case they were
 * reading it against.
 *
 * The query goes to the server, which matches term-wise across code, short
 * descriptor and full descriptor. So "screening colonoscopy" and "colonoscopy
 * screening" both find the code, which a substring search does not.
 */
function CodebookLookupField({
  onPick,
  current,
}: {
  onPick: (code: string) => void;
  current: string;
}) {
  const [q, setQ] = useState("");
  const search = useCodebookSearch(q);
  const trimmed = q.trim();
  // Excluding the code already on screen: offering to navigate to where you
  // already are is a dead row in a list that is capped.
  const hits = search.hits.filter(
    (h) => h.code.toUpperCase() !== current.toUpperCase(),
  );
  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Look up another code
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
        <input
          className="h-8 w-full rounded-coronis border border-border bg-card pl-7 pr-2 text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coronis-blue-500"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Code, or words from the descriptor…"
          aria-label="Search the codebook"
        />
      </div>
      {trimmed !== "" && (
        <div
          role="group"
          aria-label="Codebook search results"
          className="max-h-[200px] overflow-auto rounded-coronis border border-border"
        >
          {search.status === "loading" && (
            <p className="m-0 px-2.5 py-2 text-[12px] text-muted-foreground">
              Searching…
            </p>
          )}
          {/* A missing codeset and a search that found nothing are different
              answers, and only one of them is about the query. Collapsing them
              would tell a reviewer no such code exists on a deployment that
              never loaded a codeset to check. */}
          {search.status === "no-codeset" && (
            <p className="m-0 px-2.5 py-2 text-[12px] text-muted-foreground">
              No code reference is loaded, so this cannot be searched.
            </p>
          )}
          {search.status === "error" && (
            <p className="m-0 px-2.5 py-2 text-[12px] text-muted-foreground">
              The code reference could not be reached.
            </p>
          )}
          {search.status === "ready" && hits.length === 0 && (
            <p className="m-0 px-2.5 py-2 text-[12px] text-muted-foreground">
              No code matches “{trimmed}”.
            </p>
          )}
          {search.status === "ready" &&
            hits.map((h) => (
              <button
                key={h.code}
                type="button"
                onClick={() => {
                  onPick(h.code);
                  setQ("");
                }}
                className="flex w-full items-baseline gap-2 border-b border-border px-2.5 py-1.5 text-left last:border-0 hover:bg-muted"
              >
                <span className="shrink-0 font-mono text-[12.5px] font-semibold text-foreground">
                  {h.code}
                </span>
                <span className="truncate text-[12px] text-muted-foreground">
                  {h.title}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/** Searchable codebook picker — the auditor enters the correct code. Filters by
 *  `kind`, and offers "Add <RAW>" for a code that isn't in the catalog. */
export function CodePicker({
  onPick,
  placeholder,
  kind,
  autoFocus,
}: {
  onPick: (code: string) => void;
  placeholder?: string;
  kind?: CodeKind;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  // The SERVER's codeset, not the bundle's ~130 entries. The picker previously
  // could not offer a code the agent had just assigned unless someone had
  // hand-added it to the compiled-in map — on a screen whose job is letting a
  // reviewer supply the code that IS right.
  const search = useCodebookSearch(q, kind);
  const results = search.hits;
  const raw = q.trim().toUpperCase();
  // Offering "add as typed" while a lookup is still in flight invites a reviewer
  // to bypass a codeset that was about to answer.
  const canAddRaw =
    raw.length >= 2 &&
    search.status !== "loading" &&
    !results.some((h) => h.code.toUpperCase() === raw);
  const pick = (code: string) => {
    onPick(code);
    setQ("");
    setOpen(false);
  };
  return (
    <div ref={ref} className="relative min-w-0">
      <div className="flex items-center gap-1">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
          <input
            className="h-8 w-full rounded-coronis border border-border bg-card pl-7 pr-2 text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coronis-blue-500"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            autoFocus={autoFocus}
            placeholder={placeholder ?? "Search code or description…"}
            aria-label="Search the codebook"
          />
        </div>
        {canAddRaw && (
          <button
            type="button"
            onClick={() => pick(raw)}
            title="Use exactly what you typed"
            className="inline-flex h-8 shrink-0 items-center rounded-coronis border border-border bg-card px-2 text-[11.5px] font-medium text-foreground hover:bg-muted"
          >
            Add {raw}
          </button>
        )}
      </div>
      {open && (results.length > 0 || canAddRaw) && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 max-h-[260px] overflow-auto rounded-coronis border border-border bg-card p-1 shadow-lg">
          {results.map((h) => (
            <button
              key={h.code}
              type="button"
              onClick={() => pick(h.code)}
              className="flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
            >
              <span className="shrink-0 font-mono text-[12.5px] font-semibold text-foreground">
                {h.code}
              </span>
              <span className="truncate text-[11.5px] text-muted-foreground">
                {h.title}
              </span>
              <Badge variant="outline">
                {(h.kind && KIND_LABEL[h.kind]) || "Code"}
              </Badge>
            </button>
          ))}
          {search.status === "no-codeset" && (
            // Not "no match". A reviewer told the codeset is missing knows their
            // code may well be valid; one told there is no match concludes it
            // is not, which is a claim this deployment cannot make.
            <div className="px-2 py-1.5 text-[11.5px] text-coronis-amber-700">
              No code reference is loaded on this server, so codes cannot be
              searched. You can still enter one as typed.
            </div>
          )}
          {results.length === 0 && (
            <div className="px-2 py-1.5 text-[11.5px] text-muted-foreground">
              No catalog match — use “Add {raw}” to enter it as typed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The blue "correct code(s)" entry box revealed when a field verdict is Neither. */
export function CorrectCodeEntry({
  codes,
  onChange,
  kind,
  onOpenCode,
  multi,
}: {
  codes: string[] | undefined;
  onChange: (codes: string[]) => void;
  kind?: CodeKind;
  onOpenCode?: (code: string) => void;
  multi?: boolean;
}) {
  const list = codes ?? [];
  const add = (code: string) => {
    if (!list.includes(code)) onChange([...list, code]);
  };
  const remove = (code: string) => onChange(list.filter((c) => c !== code));
  return (
    <div
      className="flex flex-col gap-1 rounded-coronis px-2 py-1.5"
      style={{
        background: "var(--chip-accent-bg)",
        border: "1px solid var(--chip-accent-border)",
      }}
    >
      <span
        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
        style={{ color: "var(--chip-accent-fg)" }}
      >
        <Check className="size-3" />{" "}
        {multi ? "Correct code(s)" : "Correct code"}
      </span>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {list.map((code) => (
            <span
              key={code}
              className="flex items-center gap-1 rounded-md bg-card py-0.5 pl-1.5 pr-0.5"
              style={{ border: "1px solid var(--chip-accent-border)" }}
            >
              <button
                type="button"
                onClick={() => onOpenCode?.(code)}
                title="Open codebook"
                className="font-mono text-xs font-semibold"
                style={{ color: "var(--chip-accent-fg)" }}
              >
                {code}
              </button>
              <button
                type="button"
                onClick={() => remove(code)}
                title="Remove"
                aria-label={`Remove ${code}`}
                className="inline-flex size-[18px] items-center justify-center rounded text-muted-foreground hover:bg-muted"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {(multi || list.length === 0) && (
        <CodePicker
          kind={kind}
          onPick={add}
          placeholder="Search the codebook for the correct code…"
        />
      )}
    </div>
  );
}

/** A square correct/flag checkbox for one multi-field code row (teal when checked). */
export function RowCheck({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  if (disabled) return <span className="size-[22px]" aria-hidden />;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      title={on ? "Marked correct — click to unmark" : "Mark this code correct"}
      className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-md transition-colors"
      style={{
        border: on
          ? "1px solid var(--ch-teal-500)"
          : "1px solid var(--border-strong)",
        background: on ? "var(--ch-teal-500)" : "var(--bg-surface)",
      }}
    >
      {on && <Check className="size-3.5" style={{ color: "#fff" }} />}
    </button>
  );
}

/** Per-code note toggle + textarea used by both multi-code and added-code rows. */
/** The title beside a reviewer-added code. Its own component so the lookup hook
 *  sits at a component boundary rather than inside a row's JSX. */
function AddedCodeTitle({ code }: { code: string }) {
  const lookup = useCodeEntry(code);
  return (
    <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted-foreground">
      {codeTitle(lookup)}
    </span>
  );
}

function CodeNote({
  rowState,
  onSet,
}: {
  rowState: MultiRowVerdict;
  onSet: (patch: Partial<MultiRowVerdict>) => void;
}) {
  const [showNote, setShowNote] = useState(!!rowState.note);
  return (
    <>
      <NoteButton
        has={!!rowState.note}
        open={showNote}
        onClick={() => setShowNote((s) => !s)}
      />
      {showNote && (
        <Textarea
          className="min-h-[38px] text-[11.5px]"
          placeholder="Note for this code…"
          value={rowState.note ?? ""}
          onChange={(e) => onSet({ note: e.target.value })}
        />
      )}
    </>
  );
}

const GRID4 = "24px minmax(0,1fr) minmax(0,1fr) 24px";

/** One multi-field union row: A/B code cells with a correct checkbox on each side,
 *  and — when flagged — a reason select + optional note. */
export function MultiCodeRow({
  field,
  row,
  rowState,
  correct,
  onToggle,
  onSet,
  onOpenCode,
  renderCell,
}: {
  field: AuditField;
  row: MultiUnionRow;
  rowState: MultiRowVerdict;
  correct: boolean;
  onToggle: () => void;
  onSet: (patch: Partial<MultiRowVerdict>) => void;
  onOpenCode: (code: string) => void;
  renderCell: (
    code: string | null,
    matched: boolean,
    slot: "a" | "b",
    onOpenCode: (c: string) => void,
  ) => React.ReactNode;
}) {
  const flagged = !row.shared || !correct;
  // A flagged code always offers its reason.
  //
  // It used to wait for an explicit toggle on the row, to keep reasons appearing
  // as the auditor worked rather than all at once. That is right for a SHARED
  // code — flagged only by a deliberate uncheck, which is itself the
  // interaction. It is wrong for a ONE-SIDED code, which is flagged by default
  // (see multiRowCorrect): its reason selector never rendered, yet
  // multiFieldComplete requires every flagged code to carry one, so the field
  // could not be completed at all. Reaching the reason meant checking the box
  // and unchecking it again — two clicks that look like they did nothing.
  //
  // Not an edge case: when one result codes nothing, EVERY code in the field is
  // one-sided. Additional diagnoses is in that state on ~40% of production
  // cases, where the coder lists diagnoses and the agent returns none.
  //
  // Hiding the selector never reduced the work — the reasons are required
  // either way — it only hid what the field still needed.
  return (
    <div
      className="flex flex-col gap-1"
      data-testid="compare-code-row"
      data-shared={row.shared}
      data-correct={correct}
    >
      <div
        className="grid items-center gap-x-2"
        style={{ gridTemplateColumns: GRID4 }}
      >
        <RowCheck on={correct} onClick={onToggle} disabled={!row.a} />
        <span className="min-w-0">
          {renderCell(row.a, row.shared, "a", onOpenCode)}
        </span>
        <span className="min-w-0">
          {renderCell(row.b, row.shared, "b", onOpenCode)}
        </span>
        <RowCheck on={correct} onClick={onToggle} disabled={!row.b} />
      </div>
      {flagged && (
        <div className="flex flex-col gap-1 pl-7">
          <div className="flex items-center gap-1">
            <ReasonSelect
              field={field}
              value={rowState.reason}
              onChange={(v) => onSet({ reason: v })}
              ariaLabel={`Reason for ${row.code}`}
            />
            <CodeNote rowState={rowState} onSet={onSet} />
          </div>
        </div>
      )}
    </div>
  );
}

/** An auditor-added correct code row (blue), with a why-add reason + optional note. */
export function AddedCodeRow({
  field,
  code,
  rowState,
  onSet,
  onRemove,
  onOpenCode,
}: {
  field: AuditField;
  code: string;
  rowState: MultiRowVerdict;
  onSet: (patch: Partial<MultiRowVerdict>) => void;
  onRemove: () => void;
  onOpenCode: (code: string) => void;
}) {
  return (
    <div
      className="flex flex-col gap-1 rounded-coronis px-2 py-1.5"
      style={{
        background: "var(--chip-accent-bg)",
        border: "1px solid var(--chip-accent-border)",
      }}
      data-testid="added-code-row"
    >
      <div className="flex items-center gap-2">
        <Badge variant="info">Add</Badge>
        <button
          type="button"
          onClick={() => onOpenCode(code)}
          title="Open codebook"
          className="font-mono text-[12.5px] font-semibold"
          style={{ color: "var(--chip-accent-fg)" }}
        >
          {code}
        </button>
        <AddedCodeTitle code={code} />
        <CodeNote rowState={rowState} onSet={onSet} />
        <button
          type="button"
          onClick={onRemove}
          title="Remove this code"
          aria-label={`Remove ${code}`}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
        >
          <X className="size-3" />
        </button>
      </div>
      <ReasonSelect
        field={field}
        value={rowState.reason}
        onChange={(v) => onSet({ reason: v })}
        placeholder="Why should this code be added?"
        ariaLabel={`Why add ${code}`}
      />
    </div>
  );
}

/** "Add correct code" affordance for multi fields. */
export function AddCodeControl({
  field,
  onAdd,
}: {
  field: AuditField;
  onAdd: (code: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  if (showAdd) {
    return (
      <CodePicker
        kind={field.kind}
        onPick={(code) => {
          onAdd(code);
          setShowAdd(false);
        }}
        autoFocus
        placeholder="Search the codebook to add the correct code…"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setShowAdd(true)}
      className="inline-flex items-center gap-1 self-start rounded-coronis px-1 text-[11.5px] font-medium text-coronis-blue-600 hover:underline"
    >
      <Plus className="size-3" /> Add correct code
    </button>
  );
}

/** The disagreeing-qualifier on/off correction control (segmented). */
export function QualifierControl({
  value,
  onChange,
  label,
}: {
  value: "on" | "off" | undefined;
  onChange: (v: "on" | "off") => void;
  label: string;
}) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-[11px] text-muted-foreground">Correct:</span>
      <span
        className="inline-flex overflow-hidden rounded-coronis border"
        role="group"
        aria-label={`Correct value for ${label}`}
        style={{ borderColor: "var(--border-default)" }}
      >
        {(["on", "off"] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={value === v}
            onClick={() => onChange(v)}
            className="px-2.5 py-1 text-[11.5px] font-semibold capitalize transition-colors"
            style={{
              background:
                value === v ? "var(--ch-blue-600)" : "var(--bg-surface)",
              color: value === v ? "#fff" : "var(--fg-tertiary)",
            }}
          >
            {v === "on" ? "On" : "Off"}
          </button>
        ))}
      </span>
    </span>
  );
}

/** Small blue/plum A/B tag re-export (kept co-located for the qualifier pills). */
export function SlotTag({ slot }: { slot: "a" | "b" }) {
  const m = SLOT_META[slot];
  return (
    <span
      className="inline-flex size-[18px] shrink-0 items-center justify-center rounded font-mono text-[10px] font-bold"
      style={{ backgroundColor: m.bg, color: m.fg }}
    >
      {m.tag}
    </span>
  );
}
